import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'

import { useCandidateManagementModal } from 'components/modals/CandidateManagementModal/hooks'
import { useDeleteCandidateModal } from 'components/modals/DeleteCandidateModal/hooks'
import { useMoveCandidateModal } from 'components/modals/MoveCandidateModal/hooks'
import { useDeleteCandidateGroupModal } from 'components/modals/DeleteCandidateGroupModal/hooks'
import { useExportCandidateGroupModal } from 'components/modals/ExportCandidateGroupModal/hooks'
import { useMoveCandidateGroupModal } from 'components/modals/MoveCandidateGroupModal/hooks'
import { getFeatureFlags } from 'ducks/auth'
import { getLocation, toProjects } from 'ducks/router'
import { useSearch, useTable } from 'hooks'
import { candidates, jobs } from 'orm'
import { checkActiveModule } from 'utils/modules'
import { getEnhancedActions, getEnhancedRowActions } from './helpers'
import { getFilterInitState, getFilterQuery } from '../helpers'
import { HIRE_STATUSES, getTabs, getFiltersConfigs } from './model'
import { getDefaultQueryParams } from './../model'

const useCandidatesTableQueries = () => {
  const dispatch = useDispatch()

  const handleFetchCandidates = React.useCallback(
    (
      { projectId, status },
      { limit, offset, ordering, search, is_favourite, hire_status },
    ) => {
      dispatch(
        candidates.fetchStart({
          project: projectId,
          is_favourite,
          hire_status,
          limit,
          offset,
          ordering,
          search,
          status,
        }),
      )
    },
    [dispatch],
  )

  const handleFetchJobs = React.useCallback(
    ({ projectId }) => {
      dispatch(jobs.fetchStart({ pk: projectId }))
    },
    [dispatch],
  )

  return {
    handleFetchCandidates,
    handleFetchJobs,
  }
}

const useJobDetailsData = state => {
  const { id } = state

  const project = useSelector(state => jobs.getItem()(state, { pk: id }))

  const { interview_template, modules } = project ?? {}

  const hasInterviewModule = checkActiveModule(modules?.interview)
  const hasInterviewTemplate = Boolean(interview_template)
  const isInterviewAvailable = R.and(hasInterviewModule, hasInterviewTemplate)

  jobs.useItem(id)

  return {
    state: {
      project: { id, ...project },
      hasInterviewModule,
      hasInterviewTemplate,
      isInterviewAvailable,
    },
  }
}

const useHandleHire = () => {
  const dispatch = useDispatch()

  const getNewHireStatus = candidate =>
    candidate.hire_status === HIRE_STATUSES.HIRED
      ? HIRE_STATUSES.DEFAULT
      : HIRE_STATUSES.HIRED

  const handleHire = React.useCallback(
    ({ candidate }) => {
      dispatch(
        candidates.patchStart({
          pk: candidate.id,
          data: { hire_status: getNewHireStatus(candidate) },
        }),
      )
    },
    [dispatch],
  )

  return { handleHire }
}

const useHandleFavorite = ({ projectId, status }) => {
  const dispatch = useDispatch()
  const { query = {} } = useSelector(getLocation)
  const { handleFetchCandidates } = useCandidatesTableQueries()

  const handleFavorite = React.useCallback(
    id => isFavorite => {
      dispatch(
        candidates.patchStart({
          pk: id,
          data: { is_favourite: isFavorite },
          postSuccessAction: () =>
            query?.is_favourite
              ? handleFetchCandidates({ projectId, status }, query)
              : null,
        }),
      )
    },
    [dispatch, query],
  )

  return { handleFavorite }
}

const useJobDetailsTabData = () => {
  const data = useSelector(candidates.getModel)

  const { count, items } = data ?? {}

  return {
    state: { count, items },
  }
}

export const useCandidateActions = (state, handlers) => {
  const {
    candidate,
    projectId,
    status,
    hasMoveCandidatesFeature,
    isAtsLocked,
  } = state
  const { handleSetCandidate } = handlers

  const handleReset = React.useCallback(() => {
    handleSetCandidate(null)
  }, [handleSetCandidate])

  const {
    handleCandidateManagementModalClose,
    handleCandidateManagementModalConfirm,
    handleCandidateManagementModalOpen,
    isCandidateManagementModalOpen,
  } = useCandidateManagementModal(
    { candidate, projectId, status },
    { handleReset },
  )

  const {
    handleMoveCandidateModalClose,
    handleMoveCandidateModalConfirm,
    handleMoveCandidateModalOpen,
    isMoveCandidateModalOpen,
  } = useMoveCandidateModal(
    { id: candidate?.id, projectId, status },
    { handleReset },
  )

  const {
    handleDeleteCandidateModalClose,
    handleDeleteCandidateModalConfirm,
    handleDeleteCandidateModalOpen,
    isDeleteCandidateModalOpen,
  } = useDeleteCandidateModal(
    { id: candidate?.id, projectId, status },
    { handleReset },
  )

  const { handleHire } = useHandleHire()

  const handleDelete = React.useCallback(
    ({ candidate }) => {
      handleSetCandidate(candidate)
      handleDeleteCandidateModalOpen()
    },
    [handleDeleteCandidateModalOpen, handleSetCandidate],
  )

  const handleEdit = React.useCallback(
    ({ candidate }) => {
      handleSetCandidate(candidate)
      handleCandidateManagementModalOpen()
    },
    [handleCandidateManagementModalOpen, handleSetCandidate],
  )

  const handleMove = React.useCallback(
    ({ candidate }) => {
      handleSetCandidate(candidate)
      handleMoveCandidateModalOpen()
    },
    [handleMoveCandidateModalOpen, handleSetCandidate],
  )

  const { handleFavorite } = useHandleFavorite({ projectId, status })

  const getActions = React.useCallback(
    state =>
      getEnhancedRowActions(
        { ...state, hasMoveCandidatesFeature, isAtsLocked },
        {
          onDelete: handleDelete,
          onEdit: handleEdit,
          onMove: handleMove,
          onHire: handleHire,
        },
      ),
    [
      handleDelete,
      handleEdit,
      handleMove,
      handleHire,
      hasMoveCandidatesFeature,
      isAtsLocked,
    ],
  )

  return {
    state: {
      isCandidateManagementModalOpen,
      isDeleteCandidateModalOpen,
      isMoveCandidateModalOpen,
    },
    handlers: {
      getActions,
      handleFavorite,
      handleCandidateManagementModalClose,
      handleCandidateManagementModalConfirm,
      handleCandidateManagementModalOpen,
      handleDeleteCandidateModalClose,
      handleDeleteCandidateModalConfirm,
      handleMoveCandidateModalClose,
      handleMoveCandidateModalConfirm,
    },
  }
}

export const useCandidateGroupActions = state => {
  const {
    projectId,
    selected,
    status,
    hasMoveCandidatesFeature,
    isAtsLocked,
  } = state

  const count = R.pipe(
    R.keys,
    R.length,
  )(selected)

  const {
    handleDeleteCandidateGroupModalClose,
    handleDeleteCandidateGroupModalConfirm,
    handleDeleteCandidateGroupModalOpen,
    isDeleteCandidateGroupModalOpen,
  } = useDeleteCandidateGroupModal({ projectId, status })

  const {
    handleExportCandidateGroupModalClose,
    handleExportCandidateGroupModalOpen,
    isExportCandidateGroupModalOpen,
  } = useExportCandidateGroupModal()

  const {
    handleMoveCandidateGroupModalClose,
    handleMoveCandidateGroupModalConfirm,
    handleMoveCandidateGroupModalOpen,
    isMoveCandidateGroupModalOpen,
  } = useMoveCandidateGroupModal({ candidates: R.keys(selected) })

  const actions = getEnhancedActions(
    { count, hasMoveCandidatesFeature, isAtsLocked },
    {
      onDelete: handleDeleteCandidateGroupModalOpen,
      onExport: handleExportCandidateGroupModalOpen,
      onMove: handleMoveCandidateGroupModalOpen,
    },
  )

  return {
    state: {
      actions,
      isDeleteCandidateGroupModalOpen,
      isExportCandidateGroupModalOpen,
      isMoveCandidateGroupModalOpen,
    },
    handlers: {
      handleDeleteCandidateGroupModalClose,
      handleDeleteCandidateGroupModalConfirm,
      handleExportCandidateGroupModalClose,
      handleMoveCandidateGroupModalClose,
      handleMoveCandidateGroupModalConfirm,
    },
  }
}

const useCandidatesTableData = state => {
  const { projectId, status = [] } = state

  const data = useSelector(candidates.getModel)

  const { count, items, meta } = data ?? {}

  const tableData = React.useMemo(
    () =>
      R.pipe(
        R.values,
        R.filter(item => {
          const isProjectFound = R.propEq('project', parseInt(projectId))
          const hasStatusIncluded = R.includes(R.prop('status', item), status)

          return R.gt(R.length(status), 0)
            ? R.and(isProjectFound, hasStatusIncluded)
            : isProjectFound
        }),
      )(items),
    [items, projectId, status],
  )

  return {
    state: { count, meta, tableData },
  }
}

export const useCandidatesTable = state => {
  const {
    columns,
    mapSortingPropToQueryParam,
    projectId,
    search,
    filter,
    status,
    tab,
  } = state

  const dispatch = useDispatch()

  const { handleFetchCandidates, handleFetchJobs } = useCandidatesTableQueries()

  const {
    state: { count, tableData, meta },
  } = useCandidatesTableData({ projectId, status })

  const handleRefetch = React.useCallback(
    ({ limit, offset, ordering, search, is_favourite, hire_status }) => {
      handleFetchCandidates(
        { projectId, status },
        { limit, offset, ordering, search, is_favourite, hire_status },
      )
      handleFetchJobs({ projectId })
    },
    [projectId, status, handleFetchCandidates, handleFetchJobs],
  )

  const handleQueriesUpdate = React.useCallback(
    ({ limit, offset, ordering, search, is_favourite, hire_status }) => {
      dispatch(
        toProjects(projectId, tab, {
          limit,
          offset,
          ordering,
          search,
          is_favourite,
          hire_status,
        }),
      )
    },
    [projectId, tab, dispatch],
  )

  const handleSelection = React.useCallback(
    payload => {
      dispatch(candidates.setSelection(payload))
    },
    [dispatch],
  )

  const filterQuery = React.useMemo(
    () => getFilterQuery(filter, getFiltersConfigs()),
    [filter],
  )

  const {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
      table: { toggleAllRowsSelectedHandler },
    },
  } = useTable({
    columns,
    data: tableData,
    mapSortingPropToQueryParam,
    pageCount: count,
    rowSelection: meta?.selected,
    search,
    filterQuery,
    fetchAction: handleRefetch,
    updateQueriesAction: handleQueriesUpdate,
    onRowSelectionChange: handleSelection,
  })

  return {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
      table: { toggleAllRowsSelectedHandler },
    },
  }
}

export const useCandidateState = () => {
  const featureFlags = useSelector(getFeatureFlags)
  const { candidates } = useSelector(state => state.orm)

  const [candidate, setCandidate] = React.useState(null)

  const selected = candidates?.meta?.selected ?? {}
  const hasMoveCandidatesFeature = featureFlags?.moveCandidates

  const selectedCandidates = candidates?.meta?.selected
    ? Object.keys(candidates.meta.selected).map(id => candidates.items[id])
    : []

  return {
    state: {
      candidate,
      selected,
      selectedCandidates,
      hasMoveCandidatesFeature,
    },
    handlers: { setCandidate },
  }
}

export const useFilter = () => {
  const [filter, setFilter] = React.useState(
    R.reduce(getFilterInitState(), {}, getFiltersConfigs()),
  )

  return {
    state: { filter },
    handlers: { setFilter },
  }
}

export const useJobDetailsTab = () => {
  const [search, setSearch] = useSearch()

  const {
    state: { count, items },
  } = useJobDetailsTabData()

  return {
    state: { count, items, search },
    handlers: { setSearch },
  }
}

export const useJobDetails = () => {
  const {
    payload: { id, tab: activeTab },
  } = useSelector(getLocation)
  const dispatch = useDispatch()

  const {
    state: {
      project,
      hasInterviewModule,
      hasInterviewTemplate,
      isInterviewAvailable,
    },
  } = useJobDetailsData({ id })

  const tabs = getTabs({ hasInterviewModule })

  const handleTabChange = React.useCallback(
    activeTab => {
      dispatch(toProjects(id, activeTab, getDefaultQueryParams({ activeTab })))
    },
    [id, dispatch],
  )

  return {
    state: {
      activeTab,
      project,
      tabs,
      hasInterviewModule,
      hasInterviewTemplate,
      isInterviewAvailable,
    },
    handlers: { handleTabChange },
  }
}
